import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  View,
  Text,
  Image,
  TextInput,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  SafeAreaView,
  Platform,
  Pressable,
} from 'react-native'
import { useSx } from 'dripsy'
import { makeUseAxiosHook } from 'app/redux/store'
import { Icon } from 'app/components/icon/icon'
import { useRouter } from 'solito/router'
import { useDebounce } from 'app/redux/hooks/use-debounce';
const FeedSearchDetails = ({ handleCloseModal = () => {} }) => {
  const categories = ['Biryani', 'Paneer masala', 'Gulam Jamun', 'Gajar Hal']
  const [searchText, setSearchText] = useState('')
  const { push } = useRouter()
  const sx = useSx()

  // // Debounced search handler
  const handleSearch = (text) => {
    setSearchText(text)
  }

  return (
    <SafeAreaView style={{ flex: 1, padding: 10 }}>
      {/* Search Input (Fixed) */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: 20,
          paddingHorizontal: 10,
          marginBottom: 10,
        }}
      >
        <Icon height={18} width={18} name="feedSearch" />
        <TextInput
          placeholder="Search"
          value={searchText}
          onChangeText={handleSearch}
          style={[
            {
              flex: 1,
              height: 40,
              fontSize: 16,
              borderColor: '#E8E6FA',
              borderRadius: 20,
              fontWeight: '400',
              marginLeft: 3,
            },
            sx({ flex: 1, color: '#000' }),
            Platform.OS === 'web' ? sx({ outline: 'none' }) : {},
          ]}
        />
      </View>

      {/* Category Filters (Fixed) */}
      <FlatList
        horizontal
        data={categories}
        keyExtractor={(item, index) => item + index}
        showsHorizontalScrollIndicator={false}
        style={{ maxHeight: 50 }}
        renderItem={({ item }) =>{
          return  <TouchableOpacity
          style={{
            borderRadius: 6,
            borderWidth: 1,
            padding: 8,
            borderColor: '#3C3C432E',
            margin: 2,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPress={() => {
            setSearchText(item)
            handleSearch(item)
          }}
        >
          <Text
            style={sx({
              color: '#000',
              fontSize: 14,
              fontWeight: '600',
            })}
          >
            {item}
          </Text>
        </TouchableOpacity>
        }}
      />

      {/* Image Grid (Scrollable) */}
      <View style={{ flex: 1 }}>
        <FeedSearchList
          searchText={searchText}
          handleCloseModal={handleCloseModal}
        />
      </View>
    </SafeAreaView>
  )
}

export default FeedSearchDetails

const FeedSearchList = ({ handleCloseModal, searchText }) => {
  const { push } = useRouter()
  const debouncedSearchTerm = useDebounce(searchText);
  const sx = useSx()
  const [listData, setListData] = useState({
    data: [],
    hasMore: true,
    refetch: true,
    loadMore: false,
    total: 0,
  })

  const [{ loading, data, error }, fetchData] = makeUseAxiosHook('default', {
    manual: true,
  })({
    url: `/api/v1/feed-search`,
    method: 'GET',
  })

  useEffect(() => {
    if (error) {
      return
    }

    if (data?.data && !error) {
      setListData({
        ...listData,
        data: listData.loadMore ? [...listData.data, ...data.data] : data.data,
        hasMore:
          data.total >
          (listData.loadMore
            ? listData.data.length + data.data.length
            : data.data.length),
        refetch: false,
        loadMore: false,
        total: data.total,
      })
    }
  }, [error, data])

  // Reset and refetch when search text changes
  useEffect(() => {
    setListData({
      data: [],
      hasMore: false,
      refetch: true,
      loadMore: false,
      total: 0,
    })
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (listData.refetch) {
      fetchData({
        params: {
          $skip: listData.loadMore ? listData.data.length : 0,
          $limit: 20,
          query_text: debouncedSearchTerm,
        },
      })
    }
  }, [listData, searchText]) // Added searchText as dependency

  const handleRefresh = () => {
    if (loading) return
    setListData({
      ...listData,
      data: [],
      hasMore: true,
      refetch: true,
      loadMore: false,
    })
  }

  const loadMore = () => {
    if (!listData.hasMore || loading) return
    setListData({
      ...listData,
      refetch: true,
      loadMore: true,
    })
  }

  const loadingImagesRef = useRef({})

  const handleImageLoadStart = (index: any) => {
    loadingImagesRef.current[index] = true
    // setForceUpdate((prev) => !prev)
  }

  const onPressImage = (item: any) => {
    if (handleCloseModal) {
      handleCloseModal()
    }

    push('/feed-details/[uploadId]', `/feed-details/${item?.object_id}`)
  }

  const handleImageLoad = (index: any) => {
    loadingImagesRef.current[index] = false
    // setForceUpdate((prev) => !prev)
  }

  return listData.data.length ? (
    <FlatList
      data={listData.data}
      scrollEnabled={true}
      numColumns={3}
      nestedScrollEnabled={true}
      onEndReachedThreshold={0.5}
      // onScroll={handleScroll}
      style={{
        flex: 1,
      }}
      onEndReached={loadMore}
      keyExtractor={(item: any) => item._id}
      contentContainerStyle={{
        paddingBottom: Platform.OS === 'web' ? 20 : 79,
        flexGrow: 1,
      }}
      renderItem={({ item, index }) => (
        <Pressable
          style={{ width: '33%', height: 100, margin: 2, position: 'relative' }}
          onPress={() => onPressImage(item)}
          key={item._id}
        >
          {loadingImagesRef.current[index] && (
            <ActivityIndicator
              size="small"
              color="#000"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: [{ translateX: -10 }, { translateY: -10 }],
              }}
            />
          )}
          <Image
            source={{ uri: item.url }}
            onLoadStart={() => handleImageLoadStart(index)}
            onLoad={() => handleImageLoad(index)}
            style={{ width: '100%', height: '100%', borderRadius: 0 }}
            resizeMode="cover"
            alt="Image"
          />
        </Pressable>
      )}
      // ItemSeparatorComponent={() => <View style={styles.separator} />}
      onRefresh={handleRefresh}
      refreshing={loading && !listData.loadMore}
      // contentContainerStyle={styles.feedContainer}

      ListFooterComponentStyle={sx({
        marginBottom: [100, 50],
      })}
      ListFooterComponent={() => {
        return (
          <>
            {loading && listData.loadMore && (
              <ActivityIndicator
                style={{ marginTop: 10 }}
                size="small"
                color="#0000ff"
              />
            )}
            {!listData.hasMore && (
              <Text
                style={{
                  textAlign: 'center',
                  marginVertical: 20,
                  color: '#888',
                }}
              >
                No more posts to load
              </Text>
            )}
          </>
        )
      }}
      ListEmptyComponent={() =>
        !loading && !listData.data.length ? (
          <Text>No results found</Text>
        ) : (
          <></>
        )
      }
    />
  ) : (
    <View style={sx({ flex: 1, justifyContent: 'center', alignItems: 'center',maxHeight :[200,'100%'] })}>
      {!loading && !listData.data.length ? (<Text>No results found</Text>
      ) : ( loading ?
      <ActivityIndicator
        size="small"
        color="#000"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: [{ translateX: -10 }, { translateY: -10 }],
        }}
      />
      : <></>)}
    </View>
  )
}
